/**
 * Permissions of the budgeting module, which are its own (API
 * `w4m_budget_*` tables) and not intranet ACLs: `can()` and the host router
 * guard know nothing about them. One observable state, loaded once per
 * session from `GET quotes/me/permissions`.
 */
import Vue from "vue";
import { INBOX_ACCESS } from "./config";
import { refreshInboxCount, resetInboxCount } from "./inbox";
import quotesService from "./services/quotes.service";

const state = Vue.observable({
  loaded: false,
  loading: false,
  superuser: false,
  keys: [],
});

let pending = null;

/** Loads (or reloads) the effective permissions of the logged user. */
export function loadBudgetingPermissions() {
  if (pending) {
    return pending;
  }
  state.loading = true;
  pending = quotesService
    .myPermissions()
    .then((response) => {
      state.superuser = !!(response && response.superuser);
      state.keys = response && Array.isArray(response.permissions) ? response.permissions : [];
      state.loaded = true;
      if (budgetingCanAny(INBOX_ACCESS)) {
        refreshInboxCount();
      }
      return state;
    })
    .finally(() => {
      state.loading = false;
      pending = null;
    });
  return pending;
}

export function resetBudgetingPermissions() {
  resetInboxCount();
  state.loaded = false;
  state.loading = false;
  state.superuser = false;
  state.keys = [];
}

export function budgetingPermissionsState() {
  return state;
}

export function budgetingCan(key) {
  return state.superuser || state.keys.includes(key);
}

export function budgetingCanAny(keys) {
  return state.superuser || keys.some((key) => state.keys.includes(key));
}

export function budgetingCanAll(keys) {
  return state.superuser || keys.every((key) => state.keys.includes(key));
}

/** Mixin for the module's components: `bcan(key)`, `bcanAny(...keys)`. */
export const budgetingAccess = {
  computed: {
    budgetingPermissions() {
      return state;
    },
  },
  methods: {
    bcan(key) {
      return budgetingCan(key);
    },
    bcanAny(...keys) {
      return budgetingCanAny(keys.flat());
    },
  },
};

/**
 * `beforeEnter` of the module's routes. Reads `meta.budgeting`
 * (`{ permissions: [...] }` all-of, `{ permissionsAny: [...] }` any-of),
 * waits for the permissions when they are not loaded yet, and sends to
 * `/forbidden` when the user lacks them. A route without `meta.budgeting`
 * only needs a session.
 */
export function budgetingGuard(to, from, next) {
  const check = () => {
    const meta = to.meta && to.meta.budgeting;
    if (!meta) {
      return next();
    }
    const all = Array.isArray(meta.permissions) ? meta.permissions : [];
    const any = Array.isArray(meta.permissionsAny) ? meta.permissionsAny : [];
    const allowed =
      (all.length === 0 || budgetingCanAll(all)) && (any.length === 0 || budgetingCanAny(any));
    return allowed ? next() : next("/forbidden");
  };
  if (state.loaded) {
    return check();
  }
  return loadBudgetingPermissions().then(check, check);
}
